import React, { useState, useEffect } from 'react';
import axios from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, BookOpen, ClipboardList, Award, AlertTriangle,
  TrendingUp, Calendar, CheckCircle, XCircle, Clock
} from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourses: 0,
    publishedCourses: 0,
    totalAssignments: 0,
    completedAssignments: 0,
    overdueAssignments: 0,
    complianceRate: 0,
    activeCAPAs: 0,
    overdueCAPAs: 0,
    pendingApprovals: 0
  });
  
  const [users, setUsers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [moduleVisibility, setModuleVisibility] = useState({});

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchDashboardData();
    }
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    try {
      const [
        usersRes,
        coursesRes,
        assignmentsRes,
        complianceRes,
        capasRes,
        auditRes
      ] = await Promise.all([
        axios.get('/users'),
        axios.get('/training/courses-enhanced'),
        axios.get('/training/assignments-enhanced'),
        axios.get('/training/compliance/overall'),
        axios.get('/capas'),
        axios.get('/audit-trail/auth/system').catch(() => ({ data: [] }))
      ]);

      const publishedCourses = coursesRes.data.filter(c => c.is_published);
      const completedAssignments = assignmentsRes.data.filter(a => a.status === 'completed');
      const overdueAssignments = assignmentsRes.data.filter(a => 
        new Date(a.due_date) < new Date() && a.status !== 'completed'
      );

      setStats({
        totalUsers: usersRes.data.length,
        totalCourses: coursesRes.data.length,
        publishedCourses: publishedCourses.length,
        totalAssignments: assignmentsRes.data.length,
        completedAssignments: completedAssignments.length,
        overdueAssignments: overdueAssignments.length,
        complianceRate: complianceRes.data.compliance_rate || 0,
        activeCAPAs: capasRes.data.filter(c => c.status !== 'closed').length,
        overdueCAPAs: capasRes.data.filter(c => c.status === 'overdue').length,
        pendingApprovals: 0
      });

      setUsers(usersRes.data);
      setRecentActivity(auditRes.data.slice(0, 10));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.patch(`/users/${userId}`, { role: newRole });
      alert('User role updated successfully!');
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Error updating user role');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 font-semibold text-2xl mb-2">🔒 Admin Access Required</p>
          <p className="text-red-600">This dashboard is only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900">Admin Control Center</h1>
        <p className="text-gray-600 mt-2">Complete system oversight and management</p>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalUsers}</p>
            </div>
            <Users className="text-blue-500" size={40} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Published Courses</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.publishedCourses}</p>
              <p className="text-xs text-gray-500 mt-1">of {stats.totalCourses} total</p>
            </div>
            <BookOpen className="text-green-500" size={40} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Compliance Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.complianceRate}%</p>
              <p className="text-xs text-gray-500 mt-1">{stats.completedAssignments}/{stats.totalAssignments} completed</p>
            </div>
            <Award className="text-purple-500" size={40} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Overdue Items</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{stats.overdueAssignments}</p>
              <p className="text-xs text-gray-500 mt-1">Training assignments</p>
            </div>
            <AlertTriangle className="text-red-500" size={40} />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/employees')}
            className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Users className="mx-auto mb-2" size={24} />
            <span className="text-sm font-semibold">Employee Management</span>
          </button>
          <button
            onClick={() => navigate('/sops')}
            className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors"
          >
            <BookOpen className="mx-auto mb-2" size={24} />
            <span className="text-sm font-semibold">SOP Management</span>
          </button>
          <button
            onClick={() => navigate('/capas')}
            className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <ClipboardList className="mx-auto mb-2" size={24} />
            <span className="text-sm font-semibold">Manage CAPAs</span>
          </button>
          <button
            onClick={() => navigate('/reports')}
            className="bg-orange-600 text-white p-4 rounded-lg hover:bg-orange-700 transition-colors"
          >
            <TrendingUp className="mx-auto mb-2" size={24} />
            <span className="text-sm font-semibold">View Reports</span>
          </button>
        </div>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Training Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <Calendar className="mr-2" size={24} />
            Training Status
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Assignments</span>
              <span className="font-bold">{stats.totalAssignments}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center">
                <CheckCircle className="text-green-500 mr-1" size={16} />
                Completed
              </span>
              <span className="font-bold text-green-600">{stats.completedAssignments}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center">
                <Clock className="text-yellow-500 mr-1" size={16} />
                In Progress
              </span>
              <span className="font-bold text-yellow-600">
                {stats.totalAssignments - stats.completedAssignments - stats.overdueAssignments}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center">
                <XCircle className="text-red-500 mr-1" size={16} />
                Overdue
              </span>
              <span className="font-bold text-red-600">{stats.overdueAssignments}</span>
            </div>
          </div>
        </div>

        {/* CAPA Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <ClipboardList className="mr-2" size={24} />
            CAPA Status
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active CAPAs</span>
              <span className="font-bold">{stats.activeCAPAs}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center">
                <AlertTriangle className="text-red-500 mr-1" size={16} />
                Overdue CAPAs
              </span>
              <span className="font-bold text-red-600">{stats.overdueCAPAs}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending Approvals</span>
              <span className="font-bold text-orange-600">{stats.pendingApprovals}</span>
            </div>
          </div>
        </div>
      </div>

      {/* User Management */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-2xl font-bold mb-4">User Management</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{u.name || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{u.email || u.id.substring(0, 20)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 capitalize">{u.department || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={u.role || 'user'}
                      onChange={(e) => updateUserRole(u.id, e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="quality_analyst">Quality Analyst</option>
                      <option value="engineer">Engineer</option>
                      <option value="user">User</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button className="text-blue-600 hover:text-blue-800">View Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Module Visibility Control */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-2xl font-bold mb-4">Module Visibility Control</h3>
        <p className="text-gray-600 mb-4">Configure which modules each role can access</p>
        <div className="text-sm text-blue-600">
          ℹ️ Coming soon: Drag-and-drop module visibility configuration
        </div>
      </div>
    </div>
  );
}
